import { ActionReducerMapBuilder, createAsyncThunk } from "@reduxjs/toolkit";
import { graphqlRequest } from "context/graphql/functions";
import { CommentInputType } from "operations/schema/schema";
import { AppAsyncThunkConfig } from "store";
import { State } from "./comments.store";

export const createAppAsyncThunk = createAsyncThunk.withTypes<AppAsyncThunkConfig>();

export const asyncMutations = {
  markAsRead: createAppAsyncThunk(
    "notifications/markAsRead",
    async (props: { notificationIds: number[] }, { rejectWithValue, extra: { sdk } }) => {
      const { notificationIds } = props;
      const { data, errors } = await graphqlRequest(sdk.markCommentNotificationRead, {
        thunkName: "notifications/markAsRead",
        thunkProps: props,
        variables: {
          notificationIds,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.markCommentNotificationRead) return rejectWithValue("something went wrong");
      return data;
    },
  ),
  addComment: createAppAsyncThunk(
    "comment/addComment",
    async (props: { comment: CommentInputType }, { rejectWithValue, extra: { sdk } }) => {
      const { comment } = props;
      const { data, errors, queued } = await graphqlRequest(sdk.insertComment, {
        thunkName: "comment/addComment",
        thunkProps: props,
        variables: {
          comment,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.insertComment) return rejectWithValue("something went wrong");
      return { data, queued };
    },
  ),
  updateComment: createAppAsyncThunk(
    "comment/updateComment",
    async (props: { comment: CommentInputType }, { rejectWithValue, extra: { sdk } }) => {
      const { comment } = props;
      const { data, errors, queued } = await graphqlRequest(sdk.updateComment, {
        thunkName: "comment/updateComment",
        thunkProps: props,
        variables: {
          comment,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.updateComment) return rejectWithValue("something went wrong");
      return { data, queued };
    },
  ),
  deleteComment: createAppAsyncThunk(
    "comment/deleteComment",
    async (props: { commentId: number }, { rejectWithValue, extra: { sdk } }) => {
      const { commentId } = props;
      const { data, errors, queued } = await graphqlRequest(sdk.deleteComment, {
        thunkName: "comment/deleteComment",
        thunkProps: props,
        variables: {
          commentId,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.deleteComment) return rejectWithValue("something went wrong");
      return { data, queued };
    },
  ),
};

export const mutationBuilder = (builder: ActionReducerMapBuilder<State>) => {
  builder.addCase(asyncMutations.markAsRead.pending, (state) => {
    state.loading = true;
    return state;
  });
  builder.addCase(asyncMutations.markAsRead.rejected, (state) => {
    state.loading = false;
    return state;
  });
  builder.addCase(asyncMutations.markAsRead.fulfilled, (state) => {
    state.loading = false;
    return state;
  });
  builder.addCase(asyncMutations.addComment.pending, (state, { meta }) => {
    if (meta.queued) return state;
    state.loading = true;
    return state;
  });
  builder.addCase(asyncMutations.addComment.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    state.loading = false;
  });
  builder.addCase(asyncMutations.addComment.fulfilled, (state) => {
    state.loading = false;
    return state;
  });
  builder.addCase(asyncMutations.updateComment.pending, (state, { meta }) => {
    if (meta.queued) return state;
    state.loading = true;
    return state;
  });
  builder.addCase(asyncMutations.updateComment.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    state.loading = false;
  });
  builder.addCase(asyncMutations.updateComment.fulfilled, (state) => {
    state.loading = false;
    return state;
  });
  builder.addCase(asyncMutations.deleteComment.pending, (state, { meta }) => {
    if (meta.queued) return state;
    state.loading = true;
    return state;
  });
  builder.addCase(asyncMutations.deleteComment.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    state.loading = false;
  });
  builder.addCase(asyncMutations.deleteComment.fulfilled, (state) => {
    state.loading = false;
    return state;
  });
};
